import React from 'react';
import { 
  Drawer, Box, Typography, IconButton, Divider,
  List, ListItem, ListItemText, ListItemSecondaryAction,
  TextField, Button, Chip, Paper
} from '@mui/material';
import { Close, Delete, LocalOffer, ShoppingCart } from '@mui/icons-material';
import { useCart } from '../context/CartContext';
import { useNavigate } from 'react-router-dom';
import noPhoto from "../assets/no_photo.webp";
import { useLanguage } from '../context/LanguageContext';
import keywordsData from '../lang/keywords.json';
import currencyTrans, { formatPrice, getCurrencySymbol, getCurrencyShort, getCurrencyFullName } from './CarrancyTrans';

const CartDrawer = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const {
    cart,
    cartOpen,
    setCartOpen,
    updateQuantity,
    removeFromCart,
    cartTotal,
    totalSavings,
    loading,
    getItemPrice,
    getOriginalPrice,
    hasDiscount,
    deliverFees,
    serviceChargePercent,
    serviceChargeValue,
    subtotalAfterThreshold,
    thresholdDiscount,
    subtotalBeforeThreshold,
    cartCurrency
  } = useCart();

  const goCheckoutTxt = language === 'ar' ? keywordsData.check_out_txt_ar : keywordsData.check_out_txt_en;
  const goCheckoutWithDiscountTxt = language === 'ar' ? keywordsData.check_out_dicount_txt_ar : keywordsData.check_out_dicount_txt_en;

  return (
    <Drawer
      anchor="right"
      dir={language === 'ar' ? 'rtl' : 'ltr'}
      open={cartOpen}
      onClose={() => setCartOpen(false)}
      PaperProps={{ 
        sx: { 
          width: 400, 
          maxWidth: '90vw', 
          p: 2,
          backgroundColor: '#000000', // Pure black background
          color: '#FFFFFF',
          borderLeft: '2px solid #FFD700', // Yellow accent border
        } 
      }}
      sx={{zIndex: 1201}}
    >
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box display="flex" alignItems="center" gap={1}>
          <ShoppingCart sx={{ color: '#FFD700' }} />
          <Typography variant="h6" sx={{ color: '#FFFFFF', fontWeight: 700 }}>
            {language === 'ar' ? keywordsData.cart_txt_title_ar : keywordsData.cart_txt_title_en}
          </Typography>
          {cart.length > 0 && (
            <Chip
              label={cart.reduce((sum, item) => sum + item.quantity, 0)}
              size="small"
              sx={{
                backgroundColor: '#FFD700',
                color: '#000000',
                fontWeight: 'bold',
                minWidth: 24,
                height: 24,
              }}
            />
          )}
        </Box>
        <IconButton 
          onClick={() => setCartOpen(false)}
          sx={{
            color: '#B3B3B3',
            '&:hover': {
              color: '#FFD700',
              backgroundColor: 'rgba(255, 215, 0, 0.08)',
            }
          }}
        >
          <Close />
        </IconButton>
      </Box>

      <Divider sx={{ 
        backgroundColor: '#2D2D2D', // Dark gray divider
        height: '1px',
        mb: 2
      }} />

      {loading ? (
        <Box p={3} textAlign="center">
          <Typography sx={{ color: '#B3B3B3' }}>{language === 'ar' ? keywordsData.loading_cart_txt_ar : keywordsData.loading_cart_txt_en}</Typography>
        </Box>
      ) : cart.length === 0 ? (
        <Box p={3} textAlign="center">
          <ShoppingCart sx={{ color: '#2D2D2D', fontSize: 48, mb: 2 }} />
          <Typography sx={{ color: '#B3B3B3', mb: 2 }}>{language === 'ar' ? keywordsData.empty_cart_txt_ar : keywordsData.empty_cart_txt_en}</Typography>
          <Button
            variant="outlined"
            onClick={() => setCartOpen(false)}
            sx={{
              color: '#FFD700',
              borderColor: '#FFD700',
              '&:hover': {
                borderColor: '#FFE55C',
                backgroundColor: 'rgba(255, 215, 0, 0.08)',
              }
            }}
          >
            {language === 'ar' ? keywordsData.continue_shoping_txt_ar : keywordsData.continue_shoping_txt_en}
          </Button>
        </Box>
      ) : (
        <>
          <List sx={{ flex: 1, overflow: 'auto',minHeight:300 }}>
            {cart.map((item) => {
              const currentPrice = getItemPrice(item);
              const originalPrice = getOriginalPrice(item);
              const isDiscounted = hasDiscount(item);
              const discountPercent = isDiscounted 
                ? Math.round((1 - currentPrice / originalPrice) * 100)
                : 0;

              return (
                <Paper
                  key={item.id}
                  elevation={0}
                  sx={{
                    backgroundColor: '#0D0D0D', // Matte black
                    border: '1px solid #2D2D2D',
                    borderRadius: '12px',
                    mb: 2,
                    p: 2,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      border: '1px solid #FFD700',
                      backgroundColor: '#121212',
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    {/* Product Image */}
                    <Box sx={{ 
                      width: 80, 
                      height: 80, 
                      position: 'relative',
                      backgroundColor: '#1A1A1A',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      <img
                        src={item.product?.imageUrl || noPhoto}
                        alt={item.product?.name}
                        style={{ 
                          width: '100%', 
                          height: '100%', 
                          objectFit: 'contain',
                          borderRadius: '8px'
                        }}
                      />
                      {isDiscounted && (
                        <Chip
                          label={`-${discountPercent}%`}
                          size="small"
                          sx={{
                            position: 'absolute',
                            top: -8,
                            right: -8,
                            backgroundColor: '#FFD700',
                            color: '#000000',
                            fontWeight: 'bold',
                            fontSize: '0.7rem',
                            height: 24,
                            minWidth: 48,
                            border: '2px solid #000000',
                          }}
                        />
                      )}
                    </Box>
                    
                    {/* Product Details */}
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography 
                        variant="subtitle2" 
                        sx={{ 
                          color: '#FFFFFF',
                          fontWeight: 700,
                          fontSize: '1rem',
                          mb: 0.5,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                      >
                        {language === 'ar' ? item.product?.name : item.product?.name_en}
                      </Typography>
                      
                      {/* Price Display */}
                      <Box sx={{ mb: 1 }}>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: '#FFD700',
                            fontWeight: 700,
                            fontSize: '1.1rem',
                            display: 'inline-block',
                            ml: 1
                          }}
                        >
                          {formatPrice(currentPrice.toFixed(2),cartCurrency,language)}
                        </Typography>
                        {isDiscounted && (
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              textDecoration: 'line-through', 
                              color: '#6B6B6B',
                              fontSize: '0.75rem',
                              display: 'block'
                            }}
                          >
                            {formatPrice(originalPrice.toFixed(2),cartCurrency,language)}
                          </Typography>
                        )}
                      </Box>
                      
                      {/* Quantity Controls */}
                     <Paper
  elevation={0}
  sx={{
    display: 'inline-flex',
    alignItems: 'center',
    backgroundColor: '#0D0D0D',
    border: '1px solid #2D2D2D',
    borderRadius: '6px',
    overflow: 'hidden',
    height: '34px',
    '&:hover': {
      borderColor: '#FFD700',
    }
  }}
>
  <Button 
    size="small"
    onClick={() => updateQuantity(item.id, Math.max(1, item.quantity - 1))}
    sx={{
      minWidth: 32,
      height: '100%',
      backgroundColor: 'transparent',
      color: '#FFFFFF',
      borderRadius: 0,
      borderRight: '1px solid #2D2D2D',
      '&:hover': {
        backgroundColor: '#FFD700',
        color: '#000000',
      }
    }}
  >-</Button>
  
  <input
    type="number"
    value={item.quantity}
    onChange={(e) => {
      const newQuantity = Math.max(1, parseInt(e.target.value) || 1);
      updateQuantity(item.id, newQuantity);
    }}
    min="1"
    style={{
      width: '50px',
      height: '100%',
      border: 'none',
      textAlign: 'center',
      color: '#FFFFFF',
      backgroundColor: 'transparent',
      fontSize: '0.875rem',
      fontWeight: '500',
      outline: 'none',
      padding: '0 4px',
    }}
  />
  
  <Button 
    size="small"
    onClick={() => updateQuantity(item.id, item.quantity + 1)}
    sx={{
      minWidth: 32,
      height: '100%',
      backgroundColor: 'transparent',
      color: '#FFFFFF',
      borderRadius: 0,
      borderLeft: '1px solid #2D2D2D',
      '&:hover': {
        backgroundColor: '#FFD700',
        color: '#000000',
      }
    }}
  >+</Button>
</Paper>               </Box>     
                    {/* Total Price & Delete */}
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column',
                      alignItems: 'flex-end',
                      justifyContent: 'space-between',
                      flexShrink: 0
                    }}>
                      <Typography 
                        variant="subtitle1" 
                        sx={{ 
                          color: '#FFD700',
                          fontWeight: 800,
                          fontSize: '1.1rem',
                          whiteSpace: 'nowrap'
                        }}
                      >
                       {formatPrice((currentPrice * item.quantity).toFixed(2),cartCurrency,language)}
                      </Typography>
                      
                      <IconButton
                        size="small"
                        onClick={() => removeFromCart(item.id)}
                        sx={{
                          color: '#6B6B6B',
                          '&:hover': {
                            color: '#FF3D00',
                            backgroundColor: 'rgba(255, 61, 0, 0.08)',
                          }
                        }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                </Paper>
              );
            })}
          </List>

          <Divider sx={{ 
            backgroundColor: '#2D2D2D',
            height: '1px',
            my: 2
          }} />

          {/* Summary Section */}
          <Box sx={{ p: 1 }}>
            {/* Total Savings */}
            {totalSavings > 0 && (
              <Paper
                elevation={0}
                sx={{
                  backgroundColor: '#0D0D0D',
                  border: '1px solid #FFD700',
                  borderRadius: '12px',
                  p: 2,
                  mb: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                <LocalOffer sx={{ color: '#FFD700' }} />
                <Box>
                  <Typography variant="body2" sx={{ color: '#FFD700', fontWeight: 600 }}>
                    {language === 'ar' ? keywordsData.you_discounted_txt_ar : keywordsData.you_discounted_txt_en}
                  </Typography>
                  <Typography variant="h6" sx={{ color: '#FFD700', fontWeight: 800 }}>
                    {formatPrice(totalSavings.toFixed(2),cartCurrency,language)}
                  </Typography>
                </Box>
              </Paper>
            )}

            {/* Threshold Discount Section */}
{thresholdDiscount.thresholdvalue > 0 && (
        <Box sx={{ 
             mb: 4,
             p: 2.5,
             backgroundColor: 'rgba(255, 215, 0, 0.05)',
             borderRadius: '16px',
             border: '1px solid #2D2D2D',
             textAlign: 'center'
           }}>
    <Box>
      <Typography variant="body1" sx={{ color: '#4CAF50', fontWeight: 800 }}>
        {language === 'ar' 
          ? `وفر ${formatPrice(thresholdDiscount.thresholdvalue,cartCurrency,language)} عند شراء بـ ${formatPrice(thresholdDiscount.min_amount,cartCurrency,language)}` 
          : `Save ${formatPrice(thresholdDiscount.thresholdvalue,cartCurrency,language)} when you spend ${formatPrice(thresholdDiscount.min_amount,cartCurrency,language)}`}
      </Typography>
      {thresholdDiscount.percent > 0 && (
        <Typography variant="caption" sx={{ color: '#B3B3B3' }}>
          {language === 'ar' 
            ? `خصم ${thresholdDiscount.percent.toFixed(1)}%` 
            : `${thresholdDiscount.percent.toFixed(1)}% discount`}
        </Typography>
      )}
    </Box>
  </Box>
)}

            {/* Delivery Fees */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              mb: 1.5
            }}>
              <Typography sx={{ color: '#B3B3B3' }}>
                
                {language === 'ar' ? keywordsData.deliver_fees_txt_ar : keywordsData.deliver_fees_txt_en} :
              </Typography>
              <Typography sx={{ color: '#FFFFFF', fontWeight: 600 }}>
                {formatPrice(deliverFees.toFixed(2),cartCurrency,language)}
              </Typography>
            </Box>

            {/* Service charge box */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              mb: 1.5
            }}>
              <Typography sx={{ color: '#B3B3B3' }}>
                
                {language === 'ar' ? 'رسوم الخدمة ' : 'Service charge '}  {serviceChargePercent.toFixed(2)}% :
              </Typography>
              <Typography sx={{ color: '#FFFFFF', fontWeight: 600 }}>
                {formatPrice(serviceChargeValue.toFixed(2),cartCurrency,language)}
              </Typography>
            </Box>

            {/* Subtotal with threshold discount */}
{subtotalBeforeThreshold > 0 && thresholdDiscount.applicable && (
  <>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
      <Typography sx={{ color: '#B3B3B3' }}>
        {language === 'ar' ? 'المجموع الفرعي' : 'Subtotal'}:
      </Typography>
      <Typography sx={{ color: '#FFFFFF', fontWeight: 600 }}>
        {formatPrice(subtotalBeforeThreshold.toFixed(2),cartCurrency,language)}
      </Typography>
    </Box>
    
    {thresholdDiscount.amount > 0 && (
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography sx={{ color: '#4CAF50' }}>
          {language === 'ar' ? 'خصم الكمية' : 'Bulk Discount'}:
        </Typography>
        <Typography sx={{ color: '#4CAF50', fontWeight: 600 }}>
          -{formatPrice(thresholdDiscount.toFixed(2),cartCurrency,language)}
        </Typography>
      </Box>
    )}
  </>
)}

            {/* Order Total */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              mb: 1
            }}>
              <Typography variant="h6" sx={{ color: '#FFFFFF', fontWeight: 700 }}>
                {language === 'ar' ? keywordsData.total_txt_ar : keywordsData.total_txt_en} :
              </Typography>
              <Typography variant="h5" sx={{ 
                color: '#FFD700', 
                fontWeight: 900,
                fontSize: '1.5rem'
              }}>
                {formatPrice(cartTotal.toFixed(2),cartCurrency,language)}
              </Typography>
            </Box>

            {/* Item Count */}
            <Typography 
              variant="body2" 
              sx={{ 
                color: '#6B6B6B',
                textAlign: 'right',
                mb: 2
              }}
            >
              {cart.reduce((sum, item) => sum + item.quantity, 0)} {language === 'ar' ? keywordsData.product_txt_ar : keywordsData.product_txt_en}
            </Typography>

            {/* Checkout Button */}
            <Button
              fullWidth
              variant="contained"
              size="large"
              sx={{
                backgroundColor: '#FFD700',
                color: '#000000',
                fontWeight: 800,
                fontSize: '1.1rem',
                py: 1.5,
                borderRadius: '12px',
                '&:hover': {
                  backgroundColor: '#FFE55C',
                  boxShadow: '0 4px 12px rgba(255, 215, 0, 0.3)',
                }
              }}
              onClick={() => {
                setCartOpen(false);
                navigate('/checkout');
              }}
            >
              {totalSavings > 0 ? goCheckoutWithDiscountTxt : goCheckoutTxt}
            </Button>

            {/* Continue Shopping Button */}
            <Button
              fullWidth
              variant="outlined"
              size="large"
              sx={{
                mt: 1.5,
                color: '#FFFFFF',
                borderColor: '#2D2D2D',
                borderRadius: '12px',
                py: 1.2,
                '&:hover': {
                  borderColor: '#FFD700',
                  backgroundColor: 'rgba(255, 215, 0, 0.08)',
                  color: '#FFD700',
                }
              }}
              onClick={() => setCartOpen(false)}
            >
              {language === 'ar' ? keywordsData.continue_shoping_txt_ar : keywordsData.continue_shoping_txt_en}
            </Button>
          </Box>
        </>
      )}
    </Drawer>
  );
};

export default CartDrawer;